import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Box,
  Plus,
  Search as SearchIcon,
  Settings2,
  KeyRound,
  Globe,
  Loader2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useGetRequest } from '@/shared/hooks/requests.js';
import { PROJECTS } from '@/shared/utils/urls.js';
import { CreateProjectModal } from '@/components/CreateProjectModal.jsx';

export const MainPage = () => {
  const { t } = useTranslation(['mainPage', 'navigation']);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const loadProjects = useGetRequest({ url: PROJECTS });
  const projects = useMemo(
    () => loadProjects.response?.projects || [],
    [loadProjects.response]
  );

  useEffect(() => {
    loadProjects.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!searchQuery) return projects;
    const q = searchQuery.toLowerCase();
    return projects.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.originUrl || '').toLowerCase().includes(q)
    );
  }, [projects, searchQuery]);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                {t('navigation:projects', 'Projects')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('projects.count', { count: projects.length })}
              </p>
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="gap-2 text-white font-medium rounded-lg h-auto px-[21px] py-[10px] cursor-pointer"
              style={{ background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)' }}
            >
              <Plus className="w-4 h-4" />
              {t('projects.new')}
            </Button>
          </div>

          {/* Search */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('projects.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-muted/50 border-border"
            />
          </div>
        </div>
      </div>

      {/* Project grid */}
      <div className="flex-1 overflow-auto p-6">
        {loadProjects.loading && !projects.length ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((project) => (
              <div
                key={project.id}
                onClick={() => navigate(`/${project.id}/console`)}
                className={cn(
                  'relative rounded-2xl p-5 transition-all duration-200 bg-white dark:bg-gray-800',
                  'border border-gray-200 dark:border-gray-700',
                  'hover:shadow-md hover:-translate-y-1 cursor-pointer'
                )}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                    <Box className="w-5 h-5 text-blue-600" />
                  </div>
                  <Settings2 className="w-4 h-4 text-gray-300" />
                </div>

                <h3 className="font-medium text-base text-foreground mb-1 truncate">
                  {project.name}
                </h3>
                {project.description ? (
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                    {project.description}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground/60 mb-2">
                    {t('projects.noDescription')}
                  </p>
                )}
                {project.originUrl && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                    <Globe className="w-3 h-3 shrink-0" />
                    {project.originUrl}
                  </p>
                )}

                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <KeyRound className="w-3 h-3" />
                    {t('projects.openConsole')}
                  </span>
                  <span className="text-xs text-blue-600 font-medium">
                    {t('projects.manage')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Box className="w-16 h-16 text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {searchQuery
                ? t('projects.noResultsTitle')
                : t('projects.emptyTitle')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {searchQuery
                ? t('projects.noResultsDesc')
                : t('projects.emptyDesc')}
            </p>
            {!searchQuery && (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('projects.new')}
              </Button>
            )}
          </div>
        )}
      </div>

      <CreateProjectModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        setData={() => loadProjects.request()}
      />
    </div>
  );
};
